import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Spin } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { pick, toNumber } from "@components/health/clients/health-clients-utils";
import { fetchSprTemplateNaznItems } from "./spr-template-nazn-api";
import SprTemplateNaznItemBlock from "./spr-template-nazn-item-block";
import { normalizeCnt } from "./spr-template-nazn-utils";

const NEW_KEY = "new";

/**
 * Вкладка «Состав» — блоки строк шаблона с правкой в форме.
 */
const SprTemplateNaznSostav = ({ idShb, open, canEdit }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedKey, setExpandedKey] = useState(null);
  const [adding, setAdding] = useState(false);
  const reqRef = useRef(0);

  const loadItems = useCallback(() => {
    if (idShb == null) {
      setItems([]);
      return Promise.resolve();
    }
    const requestId = ++reqRef.current;
    setLoading(true);
    setError("");
    return fetchSprTemplateNaznItems(idShb)
      .then((data) => {
        if (requestId !== reqRef.current) return;
        setItems(Array.isArray(data) ? data.map(normalizeCnt) : []);
      })
      .catch((e) => {
        if (requestId !== reqRef.current) return;
        setItems([]);
        setError(e.message || "Не удалось загрузить состав");
      })
      .finally(() => {
        if (requestId === reqRef.current) setLoading(false);
      });
  }, [idShb]);

  useEffect(() => {
    if (!open || idShb == null) return;
    setAdding(false);
    setExpandedKey(null);
    loadItems();
  }, [open, idShb, loadItems]);

  const onToggle = (key) => {
    setExpandedKey((prev) => (prev === key ? null : key));
    if (key !== NEW_KEY) setAdding(false);
  };

  const onAdd = () => {
    if (!canEdit) return;
    setAdding(true);
    setExpandedKey(NEW_KEY);
  };

  const onItemSaved = (saved) => {
    const idStr = toNumber(pick(saved, "idStr", "IdStr"));
    setAdding(false);
    loadItems().then(() => {
      if (idStr != null) setExpandedKey(String(idStr));
    });
  };

  const onDeleted = () => {
    setExpandedKey(null);
    loadItems();
  };

  if (idShb == null) {
    return (
      <div className="spr-template-nazn-card__tab">
        <Alert type="info" showIcon message="Сначала сохраните шаблон на вкладке «Основная»" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="spr-template-nazn-card__center">
        <Spin />
      </div>
    );
  }

  return (
    <div className="spr-template-nazn-card__tab spr-template-nazn-sostav">
      {error ? (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
      ) : null}

      {canEdit ? (
        <div className="spr-template-nazn-sostav__toolbar">
          <Button type="dashed" icon={<PlusOutlined />} onClick={onAdd} disabled={adding}>
            Добавить процедуру
          </Button>
        </div>
      ) : null}

      <div className="spr-template-nazn-sostav__list">
        {adding ? (
          <SprTemplateNaznItemBlock
            idShb={idShb}
            isNew
            canEdit={canEdit}
            expanded={expandedKey === NEW_KEY}
            onToggle={() => onToggle(NEW_KEY)}
            onSaved={onItemSaved}
            onCancelNew={() => {
              setAdding(false);
              setExpandedKey(null);
            }}
          />
        ) : null}

        {items.length ? (
          items.map((item) => {
            const key = String(item.idStr);
            return (
              <SprTemplateNaznItemBlock
                key={key}
                idShb={idShb}
                item={item}
                canEdit={canEdit}
                expanded={expandedKey === key}
                onToggle={() => onToggle(key)}
                onSaved={onItemSaved}
                onDeleted={onDeleted}
              />
            );
          })
        ) : !adding ? (
          <div className="spr-template-nazn-sostav__empty">Нет строк в шаблоне</div>
        ) : null}
      </div>
    </div>
  );
};

export default SprTemplateNaznSostav;
